import type { NavigationScreenTranslationState } from '@gitbook/app-navigation';
import {
    Alert,
    LoadingPanel,
    Page,
    PageGradientWallpaper,
    PageMain,
    PanelSet,
} from '@gitbook/spine-react';

import { useFeedbackButton } from '../../../Components';
import { useSetAnalyticsScreen } from '../../../navigation';
import { apiResource, usePaginatedAPIQuery } from '../../../state';
import { TranslationsScreenLayout } from '../TranslationsScreenLayout';
import { CreateTranslatedSpaceButton } from './CreateTranslatedSpaceButton';
import { TranslationsBlankslate } from './TranslationsBlankslate';
import { TranslationsTable } from './TranslationsTable';
import { TranslationsUsageOverview } from './TranslationsUsageOverview';

/**
 * The translations overview screen.
 */
export function TranslationsOverview(props: {
    screen: NavigationScreenTranslationState & { organization: string };
}) {
    const { screen } = props;

    useSetAnalyticsScreen(
        {
            organization: screen.organization,
            screen: 'translations',
        },
        [screen.organization]
    );

    const feedbackButton = useFeedbackButton({
        flagKey: 'TRANSLATIONS',
        feedbackTitle: 'How do you like the translations feature?',
        openWithCommentVisible: true,
    });

    const { data, next, previous } = usePaginatedAPIQuery(
        apiResource('listTranslations', { organizationId: screen.organization })
    );

    if (!data) {
        return <LoadingPanel size="large" />;
    }

    if (data.items.length === 0) {
        return <TranslationsBlankslate organizationId={screen.organization} />;
    }

    return (
        <TranslationsScreenLayout
            screen={screen}
            headerControls={<CreateTranslatedSpaceButton kind="blank" />}
        >
            <Page className="flex-1">
                <PageGradientWallpaper>
                    <PageMain>
                        <PanelSet>
                            <Alert
                                size="small"
                                kind="info"
                                title="Translations are in beta"
                                details="Automated AI translations is a new beta feature. Share your feedback to help us improve it."
                                action={{
                                    kind: 'secondary',
                                    label: 'Share feedback',
                                    onPress: feedbackButton.buttonProps.onPress,
                                }}
                            />
                            <TranslationsUsageOverview organizationId={screen.organization} />
                            <TranslationsTable
                                organizationId={screen.organization}
                                translations={data.items}
                                next={next}
                                previous={previous}
                            />
                        </PanelSet>
                    </PageMain>
                </PageGradientWallpaper>
            </Page>
        </TranslationsScreenLayout>
    );
}
